import { ADMIN_ROUTE_PREFIX } from "@/types/constants/common";
import {
  CampaignOutlined,
  CreditCardOutlined,
  SchoolOutlined,
  WorkspacePremiumOutlined,
  GroupOutlined,
  HighlightOffOutlined,
  ManageAccountsOutlined,
  PersonAddAlt1Outlined,
  SupportOutlined,
  WorkOutlineOutlined,
} from "@mui/icons-material";
import {
  CERTIFIED_COLUMNS,
  FBGROUPS_COLUMNS,
  FINANCE_COLUMNS,
  LEARNER_COLUMNS,
  MARKETING_COLUMNS,
  ONBOARDING_COLUMNS,
  RESIGNED_COLUMNS,
  SPECIALIST_COLUMNS,
  STAFF_COLUMNS,
  SUPPORT_COLUMNS,
} from "./listing/columns";
import { ColDef } from "ag-grid-community";
import { DropdownItemObj } from "@/types/interfaces/navigation";
import { CALLOUTS_BASE_LINK, CALLOUT_CONFIGS, CALLOUT_TYPES, CalloutType } from "./callouts/config";

const CALLOUT_ICONS: Record<CalloutType, typeof CampaignOutlined> = {
  founder: WorkspacePremiumOutlined,
  support: SupportOutlined,
  builder: SchoolOutlined,
};
import { lazy } from "react";

const AddNewAgents = lazy(() => import("./add-new-agents/AddNewAgents"));
const ListAgents = lazy(() => import("./listing/ListAgents"));

export const AGENT_NAV_TYPES = {
  addNewAgents: "add-new-agents",
  listAgents: "agents",
  staffs: "staffs",
  resignedStaffs: "resigned",
  onboarding: "all",
  finance: "finance",
  marketing: "marketing",
  support: "support",
  specialist: "specialist",
  learner: "learner",
  certified: "certified",
  fbgroups: "fbgroups",
};

export const CERTIFICATIONS: Record<number, string> = {
  0: "I’ve no designation this time",
  5: "Avalon Waterways - Avalon Specialist",
  2: "Celebrity Cruises - Five Star Agent",
  6: "Cosmos - Cosmos Expert",
  9: "Collette Vacations - Collette Specialist",
  16: "G Adventures - G Learning",
  3: "Globus - Globus Expert",
  4: "Monograms - Monograms Expert",
  10: "Norwegian Cruise Line - NCL U Frestyle Certified Agent",
  8: "Oceania - Oceania Cruise Specialist",
  11: "Princess - Princess Commodore",
  18: "Rail Europe - Europen Rail Expert",
  14: "Rocky Mountaineer - Tracks Specialist",
  7: "Royal Caribbean - University of Wow Expert",
  13: "Sandals - Certified Sandals Specialist",
  1: "Supplier - Designation",
  12: "Trafalgar - Trafalgar Specialist",
  15: "VIA Rail - VIA Rail Agent",
  17: "Viking River Cruises - Viking River Cruises Specialist",
  19: "Accredited Cruise Counsellor, CLIA",
  20: "Luxury Cruise Certificate, CLIA",
};

export type AgentDataConfig = {
  label: string;
  value: string;
  type: string;
  selection: string;
  columns: ColDef[];
  /** Taller rows for tabs whose cells use two lines; the compact default otherwise */
  rowHeight?: number;
  /** Starts with the per-column filter row tucked away (a toolbar toggle shows it), for a cleaner list */
  hideFilterRow?: boolean;
};

export const AGENTS_TYPES = [
  {
    label: "Onboarding",
    value: "all",
    type: "agents",
    selection: "all",
    columns: ONBOARDING_COLUMNS,
  },
  {
    label: "Finance",
    value: "finance",
    type: "agents",
    selection: "finance",
    columns: FINANCE_COLUMNS,
  },
  {
    label: "Marketing",
    value: "marketing",
    type: "agents",
    selection: "marketing",
    columns: MARKETING_COLUMNS,
    rowHeight: 56,
  },
  {
    label: "Support",
    value: "support",
    type: "agents",
    selection: "support",
    columns: SUPPORT_COLUMNS,
    rowHeight: 56,
    hideFilterRow: true,
  },
  {
    label: "Specialists",
    value: "specialist",
    type: "agents",
    selection: "specialist",
    columns: SPECIALIST_COLUMNS,
  },
  {
    label: "Learners",
    value: "learner",
    type: "agents",
    selection: "learner",
    columns: LEARNER_COLUMNS,
  },
  {
    label: "Certified",
    value: "certified",
    type: "agents",
    selection: "certified",
    columns: CERTIFIED_COLUMNS,
  },
  {
    label: "FB Groups",
    value: "fbgroups",
    type: "agents",
    selection: "fbgroups",
    columns: FBGROUPS_COLUMNS,
  },
] as AgentDataConfig[];

export const RESIGNED_AGENTS_TYPES = [
  {
    label: "Resigned",
    value: "resigned",
    type: "resigned",
    selection: "resigned",
    columns: RESIGNED_COLUMNS,
  },
] as AgentDataConfig[];

export const STAFFS_TYPES = [
  {
    label: "Staff",
    value: "staff",
    type: "staff",
    selection: "",
    columns: STAFF_COLUMNS,
  },
] as AgentDataConfig[];

export const AGENT_NAV_OPTIONS = [
  {
    id: "admin.agents.add-new",
    text: "Add New Agents",
    isIndex: false,
    Icon: PersonAddAlt1Outlined,
    path: AGENT_NAV_TYPES.addNewAgents,
    link: `${ADMIN_ROUTE_PREFIX}/agents/${AGENT_NAV_TYPES.addNewAgents}`,
    component: AddNewAgents,
  },
  {
    id: "admin.agents.onboarding",
    text: "Onboarding",
    isIndex: true,
    path: AGENT_NAV_TYPES.onboarding,
    Icon: GroupOutlined,
    link: `${ADMIN_ROUTE_PREFIX}/agents`,
    component: ListAgents,
  },
  {
    id: "admin.agents.finance",
    text: "Finance",
    isIndex: false,
    path: AGENT_NAV_TYPES.finance,
    Icon: CreditCardOutlined,
    link: `${ADMIN_ROUTE_PREFIX}/agents/${AGENT_NAV_TYPES.finance}`,
    component: ListAgents,
  },
  {
    id: "admin.agents.marketing",
    text: "Marketing",
    isIndex: false,
    path: AGENT_NAV_TYPES.marketing,
    Icon: WorkOutlineOutlined,
    link: `${ADMIN_ROUTE_PREFIX}/agents/${AGENT_NAV_TYPES.marketing}`,
    component: ListAgents,
  },
  {
    id: "admin.agents.support",
    text: "Support",
    isIndex: false,
    path: AGENT_NAV_TYPES.support,
    Icon: SupportOutlined,
    link: `${ADMIN_ROUTE_PREFIX}/agents/${AGENT_NAV_TYPES.support}`,
    component: ListAgents,
  },
  {
    id: "admin.agents.staffs",
    text: "Staffs",
    isIndex: false,
    path: AGENT_NAV_TYPES.staffs,
    Icon: ManageAccountsOutlined,
    link: `${ADMIN_ROUTE_PREFIX}/agents/${AGENT_NAV_TYPES.staffs}`,
    component: ListAgents,
  },
  {
    id: "admin.agents.resigned",
    text: "Resigned Staffs",
    isIndex: false,
    path: AGENT_NAV_TYPES.resignedStaffs,
    Icon: HighlightOffOutlined,
    link: `${ADMIN_ROUTE_PREFIX}/agents/${AGENT_NAV_TYPES.resignedStaffs}`,
    component: ListAgents,
  },
  {
    id: "admin.agents.specialist",
    text: "Specialists",
    isIndex: false,
    path: AGENT_NAV_TYPES.specialist,
    Icon: PersonAddAlt1Outlined,
    link: `${ADMIN_ROUTE_PREFIX}/agents/${AGENT_NAV_TYPES.specialist}`,
    component: ListAgents,
  },
  {
    id: "admin.agents.learners",
    text: "Learners",
    isIndex: false,
    path: AGENT_NAV_TYPES.learner,
    Icon: PersonAddAlt1Outlined,
    link: `${ADMIN_ROUTE_PREFIX}/agents/${AGENT_NAV_TYPES.learner}`,
    component: ListAgents,
  },
  {
    id: "admin.agents.certified",
    text: "Certified",
    isIndex: false,
    path: AGENT_NAV_TYPES.certified,
    Icon: PersonAddAlt1Outlined,
    link: `${ADMIN_ROUTE_PREFIX}/agents/${AGENT_NAV_TYPES.certified}`,
    component: ListAgents,
  },
  {
    id: "admin.agents.fbgroups",
    text: "FB Groups",
    isIndex: false,
    path: AGENT_NAV_TYPES.fbgroups,
    Icon: PersonAddAlt1Outlined,
    link: `${ADMIN_ROUTE_PREFIX}/agents/${AGENT_NAV_TYPES.fbgroups}`,
    component: ListAgents,
  },
] as const;

export const AGENTS_SECTION_NAVIGATION: DropdownItemObj[] = [
  ...AGENT_NAV_OPTIONS.map(({ id, text, Icon, link }) => ({
    id,
    text,
    Icon,
    link,
  })),
  {
    id: "admin.agents.callouts",
    text: "Callouts",
    Icon: CampaignOutlined,
    dropDownItems: CALLOUT_TYPES.map((type) => ({
      id: `admin.agents.callouts.${type}`,
      text: CALLOUT_CONFIGS[type].label,
      Icon: CALLOUT_ICONS[type],
      link: `${CALLOUTS_BASE_LINK}/${type}`,
    })),
  },
];
